import {Group, Image, Text, UnstyledButton} from '@mantine/core';
import type {FC} from 'react';

/**
 * Row of square image thumbnails with a "+N" overflow tile past a max count.
 * @registry Thumbnail strip with overflow tile and optional click-through.
 */
export interface ThumbnailStripProps {
  items: {src: string; alt: string}[];
  max?: number;
  size?: number;
  countLabel?: string;
  onItemClick?: (index: number) => void;
}

const DEFAULT_MAX = 3;
const DEFAULT_SIZE = 48;

export const ThumbnailStrip: FC<ThumbnailStripProps> = ({
  items,
  max = DEFAULT_MAX,
  size = DEFAULT_SIZE,
  countLabel,
  onItemClick,
}) => {
  const hasOverflow = items.length > max;
  const visibleCount = hasOverflow ? Math.max(0, max - 1) : max;
  const visibleItems = items.slice(0, visibleCount);
  const hiddenCount = items.length - visibleItems.length;

  return (
    <Group gap="xs" align="center" wrap="nowrap">
      {visibleItems.map((item, index) => (
        <UnstyledButton
          key={item.src + index}
          onClick={() => onItemClick?.(index)}
          disabled={!onItemClick}
          style={{cursor: onItemClick ? 'pointer' : 'default'}}>
          <Image
            src={item.src}
            alt={item.alt}
            w={size}
            h={size}
            radius="sm"
            fit="cover"
          />
        </UnstyledButton>
      ))}
      {hiddenCount > 0 && (
        <UnstyledButton
          onClick={() => onItemClick?.(visibleCount)}
          disabled={!onItemClick}
          style={{cursor: onItemClick ? 'pointer' : 'default'}}>
          <Text
            w={size}
            h={size}
            fw={600}
            fz="sm"
            c="dimmed"
            bg="gray.1"
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            +{hiddenCount}
          </Text>
        </UnstyledButton>
      )}
      {countLabel && (
        <Text size="xs" c="dimmed">
          {countLabel}
        </Text>
      )}
    </Group>
  );
};
